// src/components/places/PlacesResultCard.tsx
//
// Renders a [PLACES_RESULT] response-type block: a list of places the
// AI recommended for "find me X" / "help me find Y" queries.
//
// The AI emits raw items with just {name, reasoning, category?}. This
// component grounds each name against Google Places via the placeSearch
// Cloud Function, then shows photo + distance + rating + open_now.
// Items the API can't resolve gracefully degrade to name + reasoning
// only (no card chrome, no tap-through that would 404).
//
// Tap-through wires into the existing onInteractiveClick handler — the
// same one that powers chip taps inside message text. Tapping a place
// asks the AI to elaborate on it, keeping the user in conversational
// flow rather than dumping them into a separate detail page.

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Skeleton,
  Stack,
  ButtonBase,
  Button,
  Collapse,
} from '@mui/material';
import {
  Place as PlaceIcon,
  Star,
  StarHalf,
  StarBorder,
  DirectionsWalk as WalkIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';
import { useT } from '../../i18n/useT';
import { FeedItemDetailSheet } from '../discover/FeedItemDetailSheet';
import type { FeedItem } from '../../hooks/useFeed';

// One item inside a [PLACES_RESULT] block, as parsed by
// parsePlacesResults() in utils/interactiveContent.ts. Re-declared
// locally to keep this component standalone — TypeScript's structural
// typing reconciles this shape with whatever the parser returns.
export interface PlacesResultItem {
  name: string;
  reasoning?: string;
  category?: string;
}

type PlaceFilteredReason = 'rating_too_low' | 'no_rating' | 'closed';

interface GroundedPlace {
  place_id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  rating: number | null;
  user_ratings_total: number | null;
  open_now: boolean | null;
  distance_m: number | null;
  walk_distance_m: number | null;
  walk_duration_s: number | null;
  photo_reference: string | null;
  maps_url: string;
  // Set when the backend's filter rejected this place. Frontend
  // moves these into the "Show others" dropdown rather than the
  // main list, with the reason annotated.
  filtered_reason: PlaceFilteredReason | null;
}

interface PlaceSearchResponse {
  result: GroundedPlace | null;
}

interface ItemState {
  loading: boolean;
  grounded: GroundedPlace | null;
  // Keep the original raw item around for fallback display + reasoning.
  raw: PlacesResultItem;
}

interface PlacesResultCardProps {
  items: PlacesResultItem[];
  /** User's current location — used to bias Places search and compute
   *  walking distances. If absent, we still resolve names but distances
   *  are unknown (label hides). */
  userLocation?: { lat: number; lng: number };
  /** Tap-through handler. We pass a follow-up prompt asking the AI to
   *  tell more about this place — same hook that chip taps use. */
  onInteractiveClick?: (followUpMessage: string) => void;
  /** Cloud Functions region/host for placePhotoProxy URL construction.
   *  Resolved at build time from VITE_FIREBASE_PROJECT_ID. */
  projectId?: string;
}

const formatDistance = (m: number | null): string | null => {
  if (m === null) return null;
  if (m < 100) return `${m} m`;
  if (m < 1000) return `${Math.round(m / 10) * 10} m`;
  return `${(m / 1000).toFixed(m < 10000 ? 1 : 0)} km`;
};

/** Walk duration: seconds → "8 min" / "1 hr 5 min" / "<1 min". */
const formatWalkTime = (seconds: number | null): string | null => {
  if (seconds === null) return null;
  if (seconds < 60) return '<1 min';
  const min = Math.round(seconds / 60);
  if (min < 60) return `${min} min`;
  const hr = Math.floor(min / 60);
  const rem = min % 60;
  return rem === 0 ? `${hr} hr` : `${hr} hr ${rem} min`;
};

/** Match the Discover SwipeCard formatting: 845, 1.2k, 12k, 1.3M. */
const formatRatingsCount = (n: number): string => {
  if (n < 1000) return String(n);
  if (n < 10000) return `${(n / 1000).toFixed(1)}k`;
  if (n < 1000000) return `${Math.round(n / 1000)}k`;
  return `${(n / 1000000).toFixed(1)}M`;
};

/** Five-star row with half-star precision. Mirrors SwipeCard's
 *  StarsRow exactly so chat rows and Discover cards look the same. */
const StarsRow: React.FC<{ rating: number; size?: number }> = ({
  rating,
  size = 13,
}) => {
  const full = Math.floor(rating);
  const hasHalf = rating - full >= 0.25 && rating - full < 0.75;
  const fullCount = rating - full >= 0.75 ? full + 1 : full;
  const finalFull = hasHalf ? full : fullCount;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {Array.from({ length: finalFull }, (_, i) => (
        <Star key={`f${i}`} sx={{ fontSize: size, color: '#f59e0b' }} />
      ))}
      {hasHalf && <StarHalf sx={{ fontSize: size, color: '#f59e0b' }} />}
      {Array.from(
        { length: 5 - finalFull - (hasHalf ? 1 : 0) },
        (_, i) => (
          <StarBorder key={`e${i}`} sx={{ fontSize: size, color: '#f59e0b' }} />
        )
      )}
    </Box>
  );
};

const photoProxyUrl = (
  photoRef: string,
  projectId: string,
  maxwidth = 400,
): string =>
  `https://us-central1-${projectId}.cloudfunctions.net/placePhotoProxy` +
  `?ref=${encodeURIComponent(photoRef)}&maxwidth=${maxwidth}`;

/** Build a discover-shaped FeedItem from a grounded place + the AI's
 *  raw recommendation. The FeedItemDetailSheet expects this shape;
 *  it'll do its own Place Details enrichment via useEntityDetail
 *  (which adds photos plural, opening hours, reviews, editorial
 *  summary, action_urls.maps_open). We supply the seed data so the
 *  sheet can render immediately while enrichment loads.
 *
 *  The id is synthetic — `recommendation:{place_id}` — used by React
 *  for keying and by usePinActions for pin state. It doesn't have to
 *  match any real feed_item id; the sheet's actions key on it
 *  per-session. */
const buildDetailItem = (
  grounded: GroundedPlace,
  raw: PlacesResultItem,
  projectId: string
): FeedItem => ({
  id: `recommendation:${grounded.place_id}`,
  source: 'recommendation',
  created_at: null,
  expires_at: null,
  title: grounded.name,
  // The sheet shows `description` as the lead body text; the AI's
  // reasoning is exactly the right thing there. If the sheet later
  // gets Google's editorial_summary via useEntityDetail and it
  // differs, both render (sheet handles dedup).
  description: raw.reasoning || '',
  image_url: grounded.photo_reference
    ? photoProxyUrl(grounded.photo_reference, projectId, 1200)
    : null,
  location: {
    lat: grounded.lat,
    lng: grounded.lng,
    address: grounded.address,
  },
  distance_m: grounded.walk_distance_m ?? grounded.distance_m ?? undefined,
  walk_time_min: grounded.walk_duration_s
    ? Math.max(1, Math.round(grounded.walk_duration_s / 60))
    : grounded.distance_m
      ? Math.max(1, Math.round(grounded.distance_m / 80))
      : undefined,
  tags: raw.category ? [raw.category] : [],
  place_id: grounded.place_id,
  chat_prompt: `Tell me more about ${grounded.name}. Why would you recommend it, and what should I expect?`,
  rationale: raw.reasoning,
  urgency: 'normal',
  is_notification: false,
  origin: 'places',
  photo_urls: grounded.photo_reference
    ? [photoProxyUrl(grounded.photo_reference, projectId, 1200)]
    : undefined,
  rating: grounded.rating ?? undefined,
  user_ratings_total: grounded.user_ratings_total ?? undefined,
  is_open_now: grounded.open_now ?? undefined,
  swipe_status: 'unseen',
});

export const PlacesResultCard: React.FC<PlacesResultCardProps> = ({
  items,
  userLocation,
  onInteractiveClick,
  projectId,
}) => {
  const t = useT();
  const [states, setStates] = useState<ItemState[]>(() =>
    items.map((raw) => ({ loading: true, grounded: null, raw }))
  );

  // Resolve project id from env if not passed. Firebase v9 SDK exposes
  // it on the app instance via a couple of paths; the simplest is the
  // VITE_ env var that's already in use elsewhere.
  const effectiveProjectId =
    projectId ||
    (import.meta as unknown as { env?: Record<string, string> }).env
      ?.VITE_FIREBASE_PROJECT_ID ||
    'travelx-46b7a';

  useEffect(() => {
    let cancelled = false;
    const placeSearch = httpsCallable<
      {
        name: string;
        near?: { lat: number; lng: number };
        category?: string;
      },
      PlaceSearchResponse
    >(functions, 'placeSearch');

    // Resolve sequentially so we don't fan-out to N parallel Places
    // requests on a slow connection. Items are rendered progressively
    // as each resolves; the order matches the AI's recommendation order
    // (which is meaningful — the first one is usually the AI's pick).
    (async () => {
      for (let i = 0; i < items.length; i++) {
        if (cancelled) return;
        const raw = items[i];
        try {
          const resp = await placeSearch({
            name: raw.name,
            near: userLocation
              ? { lat: userLocation.lat, lng: userLocation.lng }
              : undefined,
            category: raw.category,
          });
          if (cancelled) return;
          setStates((prev) => {
            const next = [...prev];
            next[i] = {
              loading: false,
              grounded: resp.data?.result || null,
              raw,
            };
            return next;
          });
        } catch (err) {
          console.warn('placeSearch failed for', raw.name, err);
          if (cancelled) return;
          setStates((prev) => {
            const next = [...prev];
            next[i] = { loading: false, grounded: null, raw };
            return next;
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // items array is rendered-once-per-message — no need to refetch

  // Detail sheet state. Tapping a row opens FeedItemDetailSheet
  // (the same surface Discover uses) populated from the grounded
  // place. The sheet does its own Place Details enrichment via
  // useEntityDetail — photos plural, opening hours, reviews,
  // editorial summary, action_urls.maps_open. From inside the sheet
  // the user can tap "Ask AI" which loops back here as a follow-up
  // chat message via onInteractiveClick (we DON'T navigate to a
  // separate route — Discover does, but we're already in chat).
  const [detailItem, setDetailItem] = useState<FeedItem | null>(null);

  const handleOpenDetail = (state: ItemState) => {
    if (!state.grounded) return;
    setDetailItem(buildDetailItem(state.grounded, state.raw, effectiveProjectId));
  };

  const handleAskAIFromSheet = (item: FeedItem) => {
    setDetailItem(null);
    if (onInteractiveClick) {
      onInteractiveClick(item.chat_prompt);
    }
  };

  if (items.length === 0) return null;

  // Split into two buckets:
  //   - visibleStates: passed all filters → main list, ranked.
  //   - excludedStates: failed a filter (rating_too_low / no_rating /
  //     closed) → behind a "Show N others" collapsible. Each row
  //     surfaces the reason so the user knows why it didn't make
  //     the cut.
  // Loading rows always go to visible (with skeletons). Truly
  // not-found groundings (grounded === null) drop entirely from
  // both buckets.
  const visibleStates = states.filter((state) => {
    if (state.loading) return true;
    if (!state.grounded) return false;
    return state.grounded.filtered_reason === null;
  });
  const excludedStates = states.filter((state) => {
    if (state.loading) return false;
    if (!state.grounded) return false;
    return state.grounded.filtered_reason !== null;
  });

  // If both buckets are empty after loading, hide the whole block.
  // (If excluded > 0 but visible === 0, we still show — the dropdown
  // becomes the primary content, auto-expanded so the user sees
  // *something* relevant rather than nothing.)
  const allDone = states.every((s) => !s.loading);
  if (allDone && visibleStates.length === 0 && excludedStates.length === 0) {
    return null;
  }

  // Auto-expand the "Show others" dropdown when we have nothing
  // to show in the main list. User asked for places, we have some,
  // they're just below the cleanliness bar — better to show them
  // than hide them. When visible > 0, the dropdown defaults to
  // collapsed and is opt-in.
  const autoExpandExcluded =
    visibleStates.length === 0 && excludedStates.length > 0;

  return (
    <Box
      sx={{
        mt: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        // Defensive: cap to parent width so a long unbreakable string
        // anywhere inside (place name, address) can't push the chat
        // bubble wider than its allowed maxWidth.
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 0.5,
          mb: 0.25,
        }}
      >
        <PlaceIcon
          sx={{ fontSize: 14, color: 'text.secondary', opacity: 0.7 }}
        />
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.68rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: 'text.secondary',
            opacity: 0.7,
          }}
        >
          {t('Places')}
        </Typography>
      </Box>

      {visibleStates.map((state, idx) => (
        <PlaceRow
          key={`${state.raw.name}-${idx}`}
          state={state}
          projectId={effectiveProjectId}
          onOpenDetail={() => handleOpenDetail(state)}
        />
      ))}

      {excludedStates.length > 0 && (
        <ExcludedDropdown
          excludedStates={excludedStates}
          projectId={effectiveProjectId}
          autoExpand={autoExpandExcluded}
          onOpenDetail={(state) => handleOpenDetail(state)}
        />
      )}

      <FeedItemDetailSheet
        open={detailItem !== null}
        item={detailItem}
        onClose={() => setDetailItem(null)}
        onAskAI={handleAskAIFromSheet}
      />
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────
// Excluded dropdown — surfaces filtered-out places with reason.
//
// Why a separate component: encapsulates expand/collapse state so
// the parent doesn't need a useState that re-renders the whole
// PlacesResultCard tree on toggle. Also lets us isolate the
// "what's the reason label for X" mapping in one place.
// ─────────────────────────────────────────────────────────────────

interface ExcludedDropdownProps {
  excludedStates: ItemState[];
  projectId: string;
  autoExpand: boolean;
  onOpenDetail: (state: ItemState) => void;
}

const ExcludedDropdown: React.FC<ExcludedDropdownProps> = ({
  excludedStates,
  projectId,
  autoExpand,
  onOpenDetail,
}) => {
  const t = useT();
  const [expanded, setExpanded] = useState(autoExpand);

  const count = excludedStates.length;
  const buttonLabel = expanded
    ? t('Hide')
    : count === 1
      ? t('Show 1 other')
      : t('Show {{n}} others').replace('{{n}}', String(count));

  return (
    <Box sx={{ mt: 0.5, width: '100%', minWidth: 0, maxWidth: '100%' }}>
      <Button
        size="small"
        startIcon={
          expanded ? (
            <ExpandLessIcon sx={{ fontSize: 16 }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 16 }} />
          )
        }
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          textTransform: 'none',
          fontSize: '0.78rem',
          fontWeight: 500,
          color: 'text.secondary',
          px: 0.75,
          py: 0.25,
          minHeight: 0,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {buttonLabel}
      </Button>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box
          sx={{
            mt: 0.75,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            // Subtle visual de-emphasis — these are explicitly
            // *not* the primary picks. Lower opacity reads as
            // "considered but not recommended" without hiding info.
            opacity: 0.78,
          }}
        >
          {excludedStates.map((state, idx) => (
            <PlaceRow
              key={`excluded-${state.raw.name}-${idx}`}
              state={state}
              projectId={projectId}
              onOpenDetail={() => onOpenDetail(state)}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

interface PlaceRowProps {
  state: ItemState;
  projectId: string;
  onOpenDetail: () => void;
}

const PlaceRow: React.FC<PlaceRowProps> = ({
  state,
  projectId,
  onOpenDetail,
}) => {
  const t = useT();
  const { loading, grounded, raw } = state;

  const handleTap = () => {
    if (!grounded) return;
    onOpenDetail();
  };

  // Microcopy bits — walk time + walk distance + open status. Rating
  // is rendered separately below as a starred row (more prominent
  // than a single star + numeric).
  const walkTime = formatWalkTime(grounded?.walk_duration_s ?? null);
  // Prefer real walking distance from the matrix, fall back to
  // straight-line haversine. The fallback is meaningfully shorter
  // (typically 30-50% less in cities), so we label it differently
  // when degraded so the user isn't misled into a wrong expectation.
  const distance = formatDistance(
    grounded?.walk_distance_m ?? grounded?.distance_m ?? null,
  );
  const distanceIsStraightLine =
    grounded !== null &&
    grounded?.walk_distance_m === null &&
    grounded?.distance_m !== null;
  const openNow = grounded?.open_now;
  const rating = grounded?.rating ?? null;
  const ratingsCount = grounded?.user_ratings_total ?? null;
  const filteredReason = grounded?.filtered_reason ?? null;

  return (
    <ButtonBase
      component="div"
      onClick={handleTap}
      disabled={!grounded}
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        textAlign: 'left',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        p: 1,
        display: 'flex',
        alignItems: 'stretch',
        gap: 1.25,
        // Final safety net — if anything inside still tries to overflow,
        // clip it rather than push the whole chat sideways.
        overflow: 'hidden',
        cursor: grounded ? 'pointer' : 'default',
        transition: 'border-color 0.15s ease, transform 0.05s ease',
        '&:hover': {
          borderColor: 'primary.main',
        },
        '&:active': {
          transform: 'scale(0.995)',
        },
        '&.Mui-disabled': {
          opacity: 1,
          cursor: 'default',
        },
      }}
    >
      {/* Thumbnail */}
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: 1.5,
          overflow: 'hidden',
          flexShrink: 0,
          bgcolor: 'action.hover',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {loading ? (
          <Skeleton
            variant="rectangular"
            width="100%"
            height="100%"
            animation="wave"
          />
        ) : grounded?.photo_reference ? (
          <Box
            component="img"
            src={photoProxyUrl(grounded.photo_reference, projectId)}
            alt={grounded.name}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <PlaceIcon sx={{ fontSize: 28, color: 'text.secondary', opacity: 0.4 }} />
        )}
      </Box>

      {/* Body */}
      <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            lineHeight: 1.3,
            // Allow up to 2 lines for long hotel/business names like
            // "Hotel NH Collection Berlin Mitte Friedrichstrasse" —
            // truncate beyond that. line-clamp is widely supported on
            // modern WebKit/Blink/Gecko via -webkit-line-clamp; on
            // older engines this degrades to no truncation, which is
            // strictly better than horizontal overflow.
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word',
          }}
        >
          {grounded?.name || raw.name}
        </Typography>

        {/* Rating row — stars + bold number + formatted count.
            Mirrors the Discover SwipeCard pattern so chat rows and
            swipe cards read the same. We render stars even when the
            place has very few reviews; the count makes the
            confidence visible. */}
        {rating !== null && (
          <Box
            sx={{
              mt: 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: 0.4,
              flexWrap: 'wrap',
            }}
          >
            <StarsRow rating={rating} />
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, ml: 0.4, fontSize: '0.78rem' }}
            >
              {rating.toFixed(1)}
            </Typography>
            {ratingsCount !== null && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.72rem',
                  color: 'text.secondary',
                  ml: 0.1,
                }}
              >
                ({formatRatingsCount(ratingsCount)})
              </Typography>
            )}
          </Box>
        )}

        {/* Microcopy chip row: walk time · walk distance · open_now.
            Each is optional; we only render if Places returned the
            field. Rating moved to its own row above for legibility.
            Walk time is the headline metric — distance is secondary
            and shown smaller. */}
        <Stack
          direction="row"
          spacing={0.75}
          sx={{ mt: 0.4, alignItems: 'center', flexWrap: 'wrap' }}
        >
          {loading && (
            <>
              <Skeleton variant="text" width={32} height={16} />
              <Skeleton variant="text" width={48} height={16} />
            </>
          )}
          {walkTime && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <WalkIcon
                sx={{ fontSize: 13, color: 'text.primary' }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.74rem',
                  fontWeight: 600,
                  color: 'text.primary',
                }}
              >
                {walkTime}
              </Typography>
              {distance && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.72rem',
                    color: 'text.secondary',
                    ml: 0.4,
                  }}
                >
                  · {distance}
                  {distanceIsStraightLine && '*'}
                </Typography>
              )}
            </Box>
          )}
          {!walkTime && distance && (
            // No walk time but we have a (haversine) distance — show
            // it alone with the asterisk to flag it's straight-line.
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <WalkIcon
                sx={{ fontSize: 12, color: 'text.secondary' }}
              />
              <Typography
                variant="caption"
                sx={{ fontSize: '0.72rem', color: 'text.secondary' }}
              >
                ~{distance}
              </Typography>
            </Box>
          )}
          {openNow === true && (
            <Chip
              label={t('Open')}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.65rem',
                fontWeight: 600,
                bgcolor: 'success.light',
                color: 'success.contrastText',
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          )}
          {/* Filter-reason chip — appears only when this row was
              excluded by the quality/availability filter. The "Show
              others" dropdown surfaces these so the user knows why
              each row didn't make the main list. */}
          {filteredReason === 'closed' && (
            <Chip
              label={t('Closed now')}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.65rem',
                fontWeight: 600,
                bgcolor: 'action.hover',
                color: 'text.secondary',
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          )}
          {filteredReason === 'rating_too_low' && (
            <Chip
              label={t('Below 3★')}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.65rem',
                fontWeight: 600,
                bgcolor: 'warning.light',
                color: 'warning.contrastText',
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          )}
          {filteredReason === 'no_rating' && (
            <Chip
              label={t('No reviews')}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.65rem',
                fontWeight: 500,
                bgcolor: 'action.hover',
                color: 'text.secondary',
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          )}
        </Stack>

        {/* The AI's reasoning. This is what makes the card feel like
            a recommendation rather than a search result. */}
        {raw.reasoning && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.5,
              fontSize: '0.78rem',
              color: 'text.secondary',
              lineHeight: 1.35,
              wordBreak: 'break-word',
            }}
          >
            {raw.reasoning}
          </Typography>
        )}
      </Box>
    </ButtonBase>
  );
};
